/**
 * V1/HARON/{PRODUCT}/{serial}/cmd — to device.
 *
 * Discriminated union of command payloads. The middleware accepts these via
 * REST `POST /devices/{id}/command` (see rest/device-ops.ts) and republishes
 * them on the MQTT cmd topic. Same schema for both wires — no drift.
 */
import { z } from 'zod';
import { CommandEnvelopeSchema } from './envelopes.js';
export const StairModeSchema = z.enum(['Auto', 'Party', 'Night', 'Guide']);
/** Changes the brightness LEVEL only — it never switches power, including
 *  `value: 0` (the darkest level, not "off"). Power on/off is SetPowerCmd's
 *  job exclusively. */
export const SetBrightnessCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_brightness'),
    value: z.number().int().min(0).max(100),
    /** CEILING only — the universal-brightness override latch. true = `value`
     *  overrides every switch's own brightness until released; false = release,
     *  switches return to their own levels. ABSENT = leave the latch unchanged
     *  (a plain level change). The device persists the latch and echoes it as
     *  CeilingStatusSchema.brightnessOverride. */
    override: z.boolean().optional(),
});
/**
 * Clock push — schedules need wall-clock time. The controller also NTP-syncs
 * on its own; this is the recovery path for networks where NTP is blocked.
 * Sent by the backend cron ~1h before any schedule boundary and on device
 * reconnect, and by the app over LAN whenever it connects locally. Idempotent
 * — a repeat send simply re-disciplines the clock.
 *
 * Understood by BOTH lighting families, for their respective schedules:
 *  - CEILING — the per-slot `onTime`/`offTime` window (DeviceSwitchSchema).
 *  - STAIR   — the Night-mode window (`nightStart`/`nightEnd` on
 *              StairConfigSchema), which the M8 controller crosses into Night
 *              at and back to Auto at.
 * Both report `clockValid` in their status so the sender can see it landed.
 */
export const TimeSyncCmd = CommandEnvelopeSchema.extend({
    type: z.literal('time_sync'),
    /** Unix epoch SECONDS (UTC). */
    epoch: z.number().int().min(1600000000),
    /** Minutes east of UTC for the device's local time (IST = 330). */
    offsetMin: z.number().int().min(-840).max(840).optional(),
    timezone: z.string().min(1).max(64).optional(),
});
/**
 * Drops ONE sensor slot from a lighting controller's slot table — Sensor
 * Setup's "delete" action for a board that is not connected. Both lighting
 * products, one command:
 *
 *  - CEILING (fw ≥ 1.1): keyed by (slot, sensorType) — ceiling slot numbers
 *    count PER TYPE (switch slots S1, S2, … and LDR slots L1, L2, … are
 *    independent sequences), so `sensorType` is REQUIRED there and must be
 *    `switch` or `ldr`. If the forgotten board ever speaks again the
 *    controller answers `UNKNOWN,<S|L><slot>` so it re-enrols fresh.
 *  - STAIR (M8 fw ≥ 1.5.5): the slot IS the wired input id (1..8, the LDR
 *    shares the same index space), so `sensorType` is informational —
 *    the app sends the reported type (`tof|pir|switch|ultrasonic|ldr`) so
 *    the backend can prune the matching report shape. The controller
 *    unpairs the slot (0xD3 to the board's uid, 60 s re-pair lockout) —
 *    this is its older `unpair_sensor` under the shared name; both work.
 *
 * Idempotent on both: forgetting a slot that does not exist acks ok, which
 * is what lets the durable offline queue replay it safely.
 *
 * "Delete from everywhere" (backend hook on relay, LAN-applied or cloud):
 *  - ceiling SWITCH slot: the controller also removes the switch bound to it
 *    (RAM + flash config, fw ≥ 1.2.0); the backend drops that slot's
 *    `switches[]` entry from the current device_config in place;
 *  - stair: the backend drops the input's `settings.sensors[]` entry —
 *    REQUIRED, because a later apply_config that still names the id makes
 *    the M8 firmware re-create the slot (applyWiredSensorConfig);
 *  - both: the slot is pruned from `reported_sensors` so every phone's list
 *    drops it at once.
 * Patterns / sequences are never touched — they are device-level. No config
 * version bump: this is not a Setup save. A board that is plugged back in
 * pairs again as a NEW input.
 */
export const ForgetSensorCmd = CommandEnvelopeSchema.extend({
    type: z.literal('forget_sensor'),
    /** Ceiling: per-type slot 1..16. Stair: wired input id 1..8. */
    slot: z.number().int().min(1).max(16),
    /** Required for ceiling (`switch`|`ldr`); the reported type for stair. */
    sensorType: z.enum(['switch', 'ldr', 'tof', 'pir', 'ultrasonic']).optional(),
});
/** Dedicated on/off for a lighting controller — previously the app just sent
 *  set_brightness with value 0 for "off", which conflated power state with
 *  brightness level. Firmware-side handling (e.g. re-applying the last
 *  configured brightness on `on: true`) is not defined by this contract. */
export const SetPowerCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_power'),
    on: z.boolean(),
});
// ── Lift v2 primitives (docs/Lift_COPLOP_API_Reference.xlsx, "Lift Flow") ──
/**
 * Stable floor identifier minted by the backend at floor save (ground → "G",
 * else category prefix B/P/F/T + per-category counter, e.g. "B1", "F3").
 * Survives renames — the user-facing floor NAME is free text and never rides
 * in commands; floorId is the only key control and status frames use.
 */
export const FloorIdSchema = z.string().min(1).max(16);
export const OnOffSchema = z.enum(['ON', 'OFF']);
export const DoorStateSchema = z.enum(['OPEN', 'CLOSED']);
/**
 * Call the lift to a floor — replaces LiftCallCmd's int-ordinal `floor`.
 * The controller resolves floorId → relay bit via the layout last delivered
 * by apply_config, and answers with lift_status frames (WAITING_DOOR →
 * MOVING → IDLE) echoing this command's cmdId.
 */
export const CallFloorCmd = CommandEnvelopeSchema.extend({
    type: z.literal('call_floor'),
    floorId: FloorIdSchema,
});
/** Cabin light — replaces lift_call aux.light. Answered by a light_status frame. */
export const SetLightCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_light'),
    state: OnOffSchema,
});
/** Cabin fan — replaces lift_call aux.fan. Answered by a fan_status frame. */
export const SetFanCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_fan'),
    state: OnOffSchema,
});
/** Door request — replaces lift_call aux.doorOpen/doorClose. The certified
 *  lift controller owns door safety; this is a request, and the real outcome
 *  arrives as door_status frames (which also fire for physical/auto changes). */
export const SetDoorCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_door'),
    state: DoorStateSchema,
});
/**
 * Stop TOGGLE — one command both engages and releases the stop latch:
 * running lift → STOPPED; stopped lift → released back to IDLE. Replaces the
 * emergency_stop / lift_reset pair for the app. Deliberately NOT owner-gated:
 * whoever may stop the lift may also release it. The physical stop button
 * behaves identically. Confirmed via lift_status (state STOPPED / IDLE).
 */
export const StopLiftCmd = CommandEnvelopeSchema.extend({
    type: z.literal('stop_lift'),
});
/**
 * Alarm/bell ON or OFF. The alarm is a STATE, not a momentary action like
 * stop_lift — so it carries ON/OFF exactly like set_light/set_fan, and the
 * same command silences it. Answered by an alarm_status frame echoing this
 * cmdId; a physical alarm press publishes the same frame with no cmdId.
 * The CONTROLLER stays the source of truth — the app must never toggle its
 * indicator locally.
 */
export const SetAlarmCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_alarm'),
    state: OnOffSchema,
});
export const LiftCallCmd = CommandEnvelopeSchema.extend({
    type: z.literal('lift_call'),
    /**
     * Omit for an aux-only command (door/fan/light with no floor change) — the
     * controller treats a present floor as a genuine call and re-pulses that
     * floor's relay, and blocks the whole command (aux included) while in
     * maintenance mode. A real floor request always sets this.
     */
    floor: z.number().int().optional(),
    /** Optional door/light/fan auxiliary requests forwarded by the certified controller. */
    aux: z
        .object({
        doorOpen: z.boolean().optional(),
        doorClose: z.boolean().optional(),
        fan: z.boolean().optional(),
        light: z.boolean().optional(),
    })
        .optional(),
});
/**
 * Latch the lift's emergency stop. The controller blocks every motion command
 * (lift_call, door aux) until a lift_reset clears it, and mirrors the latch in
 * its status (`emergency: true`). The same latch engages when the physical
 * cabin emergency button is pressed — this command is the app-side equivalent.
 */
export const EmergencyStopCmd = CommandEnvelopeSchema.extend({
    type: z.literal('emergency_stop'),
});
/** Clear a latched emergency and any fault code; resume normal operation. */
export const LiftResetCmd = CommandEnvelopeSchema.extend({
    type: z.literal('lift_reset'),
});
/**
 * Enter/leave maintenance mode. While enabled the controller rejects app
 * floor calls (errorCode MAINTENANCE_ACTIVE) but keeps reporting status, so
 * the app can show the lift as intentionally out of service rather than
 * broken. Owner-only at the REST layer.
 */
export const LiftMaintenanceCmd = CommandEnvelopeSchema.extend({
    type: z.literal('lift_maintenance'),
    enabled: z.boolean(),
});
export const SetModeCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_mode'),
    mode: StairModeSchema,
    /**
     * Independent per-mode toggle, mirroring SetPowerCmd's `on`. Modes are no
     * longer mutually exclusive on the control page: Night/Guide combine with
     * the Auto base state (and stay selected during Party — the hardware alone
     * decides what actually runs: Party = all on, Guide = first+last stair
     * held, Night = sensor triggers at night brightness, Auto = plain sensor).
     * Absent means true — the legacy single-select "switch to this mode" send,
     * which older firmware keeps understanding unchanged.
     */
    on: z.boolean().optional(),
});
/**
 * Switch which pattern is currently playing — a pure control action, like
 * SetPowerCmd/SetBrightnessCmd: no config save, no configVersion bump, and it
 * persists across reboot on the device side. Added so a pattern tap no longer
 * has to masquerade as a full ApplyConfigCmd (see that schema's doc comment);
 * `pattern` here writes the SAME internal variable as
 * `ApplyConfigCmd.settings.pattern` through one shared firmware function —
 * the two must never behave differently, last writer wins.
 *
 * Case rule: built-in names travel Capitalized ("Solid"|"Wave"|"Pulse", plus
 * legacy "Fade") and the firmware lowercases them internally — status/state
 * always reports lowercase. A user-created name is matched
 * CASE-INSENSITIVELY against what SetSequenceCmd stored; the app already
 * rejects two pattern names differing only by case (PAT-6).
 *
 * The device answers PATTERN_UNKNOWN when `pattern` is neither a built-in
 * nor a previously-stored custom name — the caller should re-send the
 * matching SetSequenceCmd and retry.
 */
export const SetPatternCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_pattern'),
    pattern: z.string().min(1).max(64),
});
export const RebootCmd = CommandEnvelopeSchema.extend({
    type: z.literal('reboot'),
});
export const ResetWifiCmd = CommandEnvelopeSchema.extend({
    type: z.literal('reset_wifi'),
});
/**
 * Device self-test (execution sweep). The app issues `self_test`
 * (device_control_screen), but the firmware's command handler is keyed on
 * `test` (`cmd == "test"`), mirrored by the app's LAN mapper which rewrites
 * self_test → {cmd:'test'} locally. BOTH spellings are accepted here — like
 * reboot/reset_wifi they carry only the envelope — and the middleware
 * normalises `self_test` → `test` at the publish boundary
 * (routing/command-router) so the firmware recognises it over the cloud path.
 */
export const SelfTestCmd = CommandEnvelopeSchema.extend({
    type: z.literal('self_test'),
});
export const TestCmd = CommandEnvelopeSchema.extend({
    type: z.literal('test'),
});
export const LocateCmd = CommandEnvelopeSchema.extend({
    type: z.literal('locate'),
    durationSec: z.number().int().min(1).max(60).optional(),
});
/**
 * Apply the complete saved setup to the device. `settings` is the stored
 * StairConfigSchema/CeilingConfigSchema/LiftConfigSchema shape verbatim,
 * MINUS `sequences` — per-stair custom pattern steps never ride inside this
 * command (they travel as their own SetSequenceCmd, sent first so a
 * newly-selected pattern is already in flash by the time `settings.pattern`
 * names it). The device applies only the keys present.
 *
 * WIRED sensors (`settings.sensors`, common/sensors.ts
 * WiredSensorSettingSchema[]): unlike `sequences`, this array DOES ride
 * inline — per-sensor config (input id → pattern/rangeCm/direction) is part
 * of the setup, replace-in-full. Any custom pattern a sensor names has
 * already arrived via its own SetSequenceCmd (same ordering rule as
 * `settings.pattern`). A config entry whose `id` matches no paired input is
 * NOT rejected: the M8 firmware materialises that slot (allocated, no uid,
 * reported `connected:false`) so the setting is not lost — which is why a
 * forgotten input must also leave `settings.sensors` (see ForgetSensorCmd).
 * An out-of-bounds range NACKs with SENSOR_RANGE_INVALID.
 *
 * ENABLE vs ACTIVE (stair `partyMode`/`nightMode`/`guideMode` in `settings`):
 * these are ENABLE flags — whether the mode is ALLOWED — always sent, all
 * three, true or false (absent only for configs saved before this existed,
 * treated as enabled). Auto has no flag: it is enabled by default, always,
 * and is never disableable — it is the floor every fallback lands on. The
 * device STORES and ENFORCES them: SetModeCmd for a disabled mode is
 * refused with errorCode MODE_DISABLED. This is distinct from `settings.mode`
 * (the single ACTIVE mode to restore) and from SetModeCmd's `on` (also
 * ACTIVE state) — `settings.mode` must never name a mode that is disabled;
 * if a disabled mode is somehow active when this is applied, the device
 * exits it back to the Auto base (enables always win).
 */
/**
 * One floor entry of a LIFT apply_config. `name` is the user's display text
 * (REST/DB column `label`, mapped at the send site) — the controller stores
 * or ignores it but must never depend on it to operate. `ordinal` is the
 * SIGNED physical position: ground = 0, below ground negative, above
 * positive. The relay/hardware index is the floor's position in the list
 * sorted by ordinal — ordinal itself is NOT a relay bit.
 */
export const ApplyConfigFloorSchema = z.object({
    floorId: FloorIdSchema,
    name: z.string().min(1).max(64),
    ordinal: z.number().int().min(-16).max(99),
});
export const ApplyConfigCmd = CommandEnvelopeSchema.extend({
    type: z.literal('apply_config'),
    // Optional since the lift shape carries `floors` instead. Lighting configs
    // always send both; the enrichment path (backend device-ops) guarantees a
    // lighting apply_config never leaves without them.
    configVersion: z.number().int().positive().optional(),
    settings: z.record(z.unknown()).optional(),
    /**
     * LIFT shape: the complete floor layout, top-level (not inside settings),
     * emitted by the backend from the floors table inside PUT /floors. Exactly
     * one of `settings` / `floors` must be present (documented, not refined —
     * a ZodEffects can't join a discriminated union). The controller validates
     * first and applies second: on any config_status REJECTED it keeps its
     * previous layout untouched.
     */
    floors: z.array(ApplyConfigFloorSchema).min(1).max(64).optional(),
    /**
     * LATEST SAVE WINS, on the controller itself (lighting; M8 fw 1.5.6+,
     * ceiling fw 1.3.2+). When the user pressed Save for this setup, in epoch
     * SECONDS. The controller remembers the `savedAt` of the setup it is
     * running and answers `CONFIG_STALE` to an apply_config whose `savedAt` is
     * OLDER — which is what an apply left in the middleware's durable queue
     * looks like once a newer setup has reached the controller by another
     * route (saved over LAN while the phone had no internet).
     *
     * The backend stamps it on every cloud apply from the stored config's save
     * time (`device_config`: `COALESCE(client_saved_at, created_at)`), so the
     * controller and the server order saves by the same clock. The app stamps
     * it on LAN applies. Absent → the controller applies as it always did.
     */
    savedAt: z.number().int().positive().optional(),
    /**
     * Apply even when `savedAt` is older than what the controller holds. Set on
     * a LAN apply (the user is doing it right now, in front of the device), on
     * the re-apply that follows the sync of an offline save (the server has
     * already decided which setup won), and on a device replace.
     */
    force: z.boolean().optional(),
});
/**
 * Turn an individual ceiling switch on or off.
 * `switchName` must match the name stored in DeviceSwitch.name (set during ceiling Setup).
 * The device uses its saved channel list for that switch.
 */
export const SetSwitchCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_switch'),
    switchName: z.string().min(1),
    isOn: z.boolean(),
    /** Brightness 0–100. If omitted the device uses the switch's last configured brightness. */
    brightness: z.number().int().min(0).max(100).optional(),
});
/**
 * Push the full set of motion sensors paired to this controller. Sent on every
 * sensor add/edit/remove so the controller always holds the complete mapping of
 * "sensor serial → detection range + which pattern to run + direction". The
 * controller reacts to each sensor's motion locally (same-router or LAN) per
 * this mapping. Replace-in-full semantics: the array IS the new complete set.
 *
 * @deprecated Superseded by `apply_config`'s `settings.sensors`
 * (common/sensors.ts WiredSensorSettingSchema) for WIRED sensors — the
 * current hardware generation, where sensors are wired to the controller and
 * identified by input index, not serial. Retained only for the legacy
 * external-Wi-Fi sensor flow (`device_sensors` CRUD still fires it); retire
 * with that flow.
 */
export const SetSensorsCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_sensors'),
    sensors: z.array(z.object({
        serial: z.string().min(1).max(64),
        // Floor is 15, not 50: the time-of-flight sensor's usable span starts at
        // 15 cm and a stair-nosing install genuinely sits in that band. The
        // per-hardware bound lives in SENSOR_RANGE_BOUNDS (rest/devices).
        rangeCm: z.number().int().min(15).max(1000),
        pattern: z.string().min(1).max(64),
        direction: z.enum(['forward', 'backward']),
    })),
});
/**
 * Store a custom pattern's full per-stair lighting sequence on the
 * controller — SAVE only, nothing visible happens. Sent at Save Setup for
 * each NEW or CHANGED pattern (diffed against the hydrated server snapshot;
 * unchanged patterns are not re-sent), BEFORE the accompanying
 * `apply_config` so a newly-selected pattern is already in flash when
 * `apply_config.settings.pattern` (or a later `SetPatternCmd`) names it.
 * Replace-in-full semantics, same as `set_sensors`: [steps] IS the complete
 * ordered sequence for [pattern] — the firmware has no other source for it.
 * Running a stored pattern is a SEPARATE step: SetPatternCmd (or
 * `apply_config.settings.pattern` for the one selected at save time).
 *
 * Scale rule: `brightness` is 0-100 (percent), same as everywhere else in
 * this contract — 0-255 never appears on any wire, the firmware converts
 * internally exactly once. (Earlier drafts of this command used the
 * firmware's raw `brite`/`delay` vocabulary; changed here for consistency
 * before any implementation existed on either side — see NOTE below.)
 *
 * Implemented on both controllers: M8_Controller_V2 (SPIFFS store, max 8
 * sequences, 8 KB parse buffer) and Ceiling_Controller_V1 (max 16, one
 * SPIFFS file per pattern). Both ack STORAGE_FULL when flash cannot hold
 * another sequence, and both keep the 64-step cap below.
 *
 * Step semantics, which the ceiling FRAME compiler relies on
 * (rest/device-ops.ts CeilingFrameSchema): a step SETS the channel to
 * `brightness` — it never toggles — so `brightness: 0` turns a lit channel
 * off, a channel may appear in several steps (the later step overrides the
 * earlier), and `delayMs: 0` means no wait (the ceiling firmware fires one
 * step per loop pass, so a run of zero-delay steps is near-simultaneous;
 * the stair firmware treats 0 as "use the device pattern speed"). Turn-off
 * on the ceiling replays the steps in reverse with every target at 0.
 */
export const SetSequenceCmd = CommandEnvelopeSchema.extend({
    type: z.literal('set_sequence'),
    /** Name of the pattern this sequence belongs to — lets the firmware key
     * playback to this name (SetPatternCmd.pattern /
     * `apply_config.settings.pattern`; on CEILING, a switch's `patternName`),
     * matched case-insensitively. */
    pattern: z.string().min(1).max(64),
    steps: z
        .array(z.object({
        /** On the stair light this is the stair number. On CEILING it is the
         *  controller output CHANNEL — same wire key deliberately, so one
         *  command and one firmware handler serve both products (see
         *  CeilingPatternStepSchema in rest/device-ops.ts, which names it
         *  `channel`). Rename it on both products together or not at all. */
        stair: z.number().int().min(1).max(32),
        brightness: z.number().int().min(0).max(100),
        delayMs: z.number().int().min(0).max(20000),
    }))
        .min(1)
        .max(64),
});
/**
 * Delete a user-created pattern's stored sequence from the controller's
 * flash — the mirror of SetSequenceCmd. Fully standalone: no configVersion,
 * no coupling to `apply_config` (the server forgets the pattern via
 * PUT /devices/:id/config; this only cleans up the copy in device flash).
 *
 * `pattern` is matched CASE-INSENSITIVELY against stored sequence names,
 * same rule as SetPatternCmd. Built-ins ("Solid"|"Wave"|"Pulse", legacy
 * "Fade") are immutable — the device NACKs them with errorCode
 * PATTERN_BUILTIN. A name with nothing stored acks OK (idempotent): the
 * middleware's durable offline queue can replay this after the pattern is
 * already gone, and the LAN + cloud legs can double-deliver — a delete
 * whose goal state is already true must not error.
 *
 * If the deleted pattern is currently playing, the device falls back to
 * "Solid" on its own (clear the in-RAM sequence, persist, publish status).
 * The app normally sends SetPatternCmd("Solid") first so this is only a
 * safety net; if a wired sensor still references the name, its motion also
 * falls back to Solid until the next apply_config corrects the mapping.
 */
export const DeleteSequenceCmd = CommandEnvelopeSchema.extend({
    type: z.literal('delete_sequence'),
    pattern: z.string().min(1).max(64),
});
/**
 * CEILING only (fw >= 1.6.0) — run an UNSAVED pattern on the real lights so
 * the user can see it before saving anything. A try-out, not a save:
 *
 *  - RAM only: nothing is written to flash, no switch is created or changed,
 *    and the controller's saved setup and live switch states are untouched.
 *  - It plays like a saved switch would (same engine): the steps walk on
 *    (or light together), hold briefly, walk off, and the preview ends by
 *    itself. A `loop` preview stops after a fixed time. `stop_preview` or
 *    master power off ends it at once; the real lights then return to what
 *    their switches say.
 *  - Runs at the device-wide brightness.
 *  - EPHEMERAL: never queued for an offline controller (a try-out that
 *    plays minutes later is worse than none) — see middleware queue-policy.
 *
 * `steps` is exactly SetSequenceCmd's shape. The style flags mirror the
 * per-switch ones in ApplyConfigCmd (sequenceEnabled / loop / fadeEnabled /
 * fadePct) so a switch can be tried with the style it will actually run.
 * Errors: POWER_OFF (master power is off), INVALID_PAYLOAD.
 */
export const PreviewSequenceCmd = CommandEnvelopeSchema.extend({
    type: z.literal('preview_sequence'),
    steps: z
        .array(z.object({
        stair: z.number().int().min(1).max(32),
        brightness: z.number().int().min(0).max(100),
        delayMs: z.number().int().min(0).max(20000),
    }))
        .min(1)
        .max(64),
    /** true (default) = walk the steps one at a time; false = all together. */
    sequenceEnabled: z.boolean().optional(),
    /** With sequenceEnabled: repeat until the preview time limit. */
    loop: z.boolean().optional(),
    fadeEnabled: z.boolean().optional(),
    fadePct: z.number().int().min(0).max(100).optional(),
});
/** Ends a `preview_sequence` at once. Idempotent — nothing running acks ok. */
export const StopPreviewCmd = CommandEnvelopeSchema.extend({
    type: z.literal('stop_preview'),
});
// Published retained to V1/HARON/{PRODUCT}/{serialNumber}/init after registration.
// Device reads this on first connect to receive its server-assigned identity
// (deviceId) and initial configuration. Runtime topics stay keyed by the
// serial; the deviceId identifies the unit in REST APIs and the database.
export const DeviceInitCmd = CommandEnvelopeSchema.extend({
    type: z.literal('device_init'),
    serialNumber: z.string().min(1).max(64),
    /** UUID assigned by the middleware. Firmware persists this and uses it as its MQTT identity. */
    deviceId: z.string().uuid(),
    configVersion: z.number().int().positive().optional(),
});
export const CmdPayloadSchema = z.discriminatedUnion('type', [
    SetBrightnessCmd,
    SetPowerCmd,
    CallFloorCmd,
    SetLightCmd,
    SetFanCmd,
    SetDoorCmd,
    StopLiftCmd,
    SetAlarmCmd,
    LiftCallCmd,
    EmergencyStopCmd,
    LiftResetCmd,
    LiftMaintenanceCmd,
    SetModeCmd,
    SetPatternCmd,
    SetSwitchCmd,
    RebootCmd,
    ResetWifiCmd,
    SelfTestCmd,
    TestCmd,
    LocateCmd,
    ApplyConfigCmd,
    SetSensorsCmd,
    SetSequenceCmd,
    DeleteSequenceCmd,
    PreviewSequenceCmd,
    StopPreviewCmd,
    TimeSyncCmd,
    ForgetSensorCmd,
    DeviceInitCmd,
]);
//# sourceMappingURL=cmd.js.map